import { action, computed, makeAutoObservable, observable } from 'mobx';
import { FormModel } from 'src_common/common/mobx-utils/Form2/FormModel';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { Common } from 'src/AppState/Common';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { IndexNowType } from 'src/AppState/ConfigState/types';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { Response200Type } from 'src/api_openapi/generated/openapi_website_cms_getAllSeoPages';
import { SdkStaff, SportsListType } from 'src/AppState/sdk/staff';
import { SelectOptionType } from 'src/domains/layouts/shared/formComponents/Select/components/new/Select';
import { CollectionType } from 'src/domains/casino/models/models';
import { timeout } from 'src_common/common/mobx-utils/timeout';
import { CmsTranslationDataModelType } from 'src/api/config/cmsTranslations/getAllCmsTranslations';
import { CmsTranslationsState } from 'src/AppState/CmsState/CmsTranslationsState/CmsTranslationsState';
import { createGuard } from 'src_common/common/createGuard';
import * as t from 'io-ts';
import {
    SeoPageTranslationFormState,
    SeoPageTranslationFormType,
} from 'src/main/cms/SeoManagement/SeoForm/SeoPageTranslationFormState';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { LanguagesState } from 'src/AppState/CmsState/LanguagesState/LanguagesState';
import { ParamsType } from 'src/api_openapi/generated/openapi_website_cms_createCmsTranslation';
// fix in LT-2383
// eslint-disable-next-line no-restricted-imports
import { apiCommon } from 'src/api/ApiCommon';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';
import { validateLength } from 'src/api/utils/form2Validators';

type CmsTranslationFormBodyType = ParamsType['body'];

const translationCompontentType = 'seo-pages';

const TranslationIO = t.interface({
    title: t.string,
    short_description: t.string,
    full_description: t.string,
});

const isTranslation = createGuard(TranslationIO);

export interface SeoPageType {
    id: number;
    universe: string;
    page_slug: string;
    title?: null | undefined | string;
    short_description?: null | undefined | string;
    full_description?: null | undefined | string;
    meta_title?: null | undefined | string;
    meta_description?: null | undefined | string;
    is_active: boolean;
}

type FormType = {
    selected_page: string;
    page_slug: string;
    title?: null | undefined | string;
    full_description?: null | undefined | string;
    short_description?: null | undefined | string;
    meta_title?: null | undefined | string;
    meta_description?: null | undefined | string;
};

type SeoPagesType = 'homepage' | 'inplay' | 'sports' | 'casino';

interface AllPagesListOptionType {
    value: SeoPagesType;
    label: string;
}

const allPages: Array<AllPagesListOptionType> = [
    {
        value: 'homepage',
        label: 'Homepage',
    },
    {
        value: 'inplay',
        label: 'In Play',
    },
    {
        value: 'sports',
        label: 'Sports Pages',
    },
    {
        value: 'casino',
        label: 'Casino Pages',
    },
];

interface CasinoListOptionType {
    value: CollectionType;
    label: string;
}

const allCasino: Array<CasinoListOptionType> = [
    {
        value: 'casino',
        label: 'Casino',
    },
    {
        value: 'live-casino',
        label: 'Live Casino',
    },
    {
        value: 'virtuals',
        label: 'Virtuals',
    },
];

export class SeoFormState {
    @observable public readonly seoPagesResource: Resource<Response200Type>;
    private readonly translationFormStateMap: MobxMapAutoNew<number, SeoPageTranslationFormState>;
    @observable private readonly allSports: Resource<SportsListType>;

    @observable public isEditMode: boolean = false;
    @observable public isDuplicateMode: boolean = false;
    @observable public showDeletePopup: boolean = false;
    @observable public showCreatedPopup: boolean = false;
    @observable public allSelectedLanguages: Array<SelectOptionType> = [];
    @observable public onEditTranslations: Array<CmsTranslationDataModelType> = [];
    @observable public formsIds: Array<number> = [];
    @observable public deleteFormIds: Array<string> = [];
    @observable public errorMessage: string | null = null;
    @observable public selectedSeoPage: SeoPagesType = 'homepage';
    @observable public singleSeoPage: SeoPageType | null = null;
    @observable public editedSeoPageId: number | null = null;
    @observable public seoPageName: string = '';

    public selectedPage: FormInputState<string, string>;
    public pageSlug: FormInputState<string, string>;
    public title: FormInputState<string, string>;
    public shortDescription: FormInputState<string, string>;
    public fullDescription: FormInputState<string, string>;
    public metaTitle: FormInputState<string, string>;
    public metaDescription: FormInputState<string, string>;
    public isActive: boolean;
    public indexNow: IndexNowType;

    private readonly trpcClient: TrpcClient;
    private readonly form: FormModel<FormType>;

    public constructor(
        private readonly sdkStaff: SdkStaff,
        public cmsTranslationsState: CmsTranslationsState,
        public languagesState: LanguagesState,
        private readonly common: Common
    ) {
        this.seoPagesResource = new Resource(async (): Promise<Response200Type> => {
            const response = await this.trpcClient.client.seoPages.getAllSeoPages.query({
                translation: 'en',
            });

            //@ts-expect-error
            return response.body ?? [];
        });
        this.translationFormStateMap = new MobxMapAutoNew(
            (_formId: number): SeoPageTranslationFormState => new SeoPageTranslationFormState()
        );
        this.allSports = new Resource(async (): Promise<SportsListType> => {
            return await this.sdkStaff.getAllSports();
        });

        this.selectedPage = FormInputState.new('').map(validateNotEmpty('This field is required.'));
        this.pageSlug = FormInputState.new('').map(validateNotEmpty('This field is required.'));
        this.title = FormInputState.new('').map(validateLength(100));
        this.shortDescription = FormInputState.new('').map(validateLength(1000));
        this.fullDescription = FormInputState.new('').map(validateLength(10000));
        this.metaTitle = FormInputState.new('');
        this.metaDescription = FormInputState.new('');
        this.isActive = false;
        this.indexNow = ConfigState.get(this.common).config.indexNow;
        this.trpcClient = this.common.trpcClient;

        this.form = FormModel.group({
            selected_page: this.selectedPage,
            page_slug: this.pageSlug,
            title: this.title,
            short_description: this.shortDescription,
            full_description: this.fullDescription,
            meta_title: this.metaTitle,
            meta_description: this.metaDescription,
        });
        makeAutoObservable(this);
    }

    @computed public get seoPagesData(): Response200Type {
        const seoPagesData = this.seoPagesResource.get();
        if (seoPagesData.type === 'ready') {
            return seoPagesData.value;
        }
        return [];
    }

    @computed public get isLoading(): boolean {
        const sportsDetailsData = this.seoPagesResource.get();
        return sportsDetailsData.type === 'loading';
    }

    public handleSubpageSelect = (value: string): void => {
        this.pageSlug.setValue(value);
    };

    public handlePageSelect = (value: string): void => {
        const selectedSeoPage = allPages.find((page) => page.value === value);

        if (selectedSeoPage !== undefined) {
            this.selectedPage.setValue(selectedSeoPage.label);
            this.selectedSeoPage = selectedSeoPage.value;
        }

        if (value === 'inplay' || value === 'homepage') {
            this.handleSubpageSelect(value);
        }
    };

    @computed private get sportList(): SportsListType {
        const allSports = this.allSports.get();
        if (allSports.type === 'ready') {
            return allSports.value;
        }
        return [];
    }

    public get sportListOptions(): Array<SelectOptionType> {
        const sportDetailsDataSlugList = this.seoPagesData.map((element) => element.page_slug);
        const availableSportsList = this.sportList.filter(
            (element) => sportDetailsDataSlugList.includes(element.id) === false
        );

        const sportsList = availableSportsList.map((elem) => {
            return {
                value: elem.id,
                label: elem.name,
            };
        });

        return sportsList;
    }

    public get casinoListOptions(): Array<CasinoListOptionType> {
        const casinoDetailsDataSlugList = this.seoPagesData.map((element) => element.page_slug);
        const availableCasinoList = allCasino.filter(
            (element) => casinoDetailsDataSlugList.includes(element.value) === false
        );

        return availableCasinoList;
    }

    public get listOptions(): Array<SelectOptionType> {
        const slugList = this.seoPagesData.map((element) => element.page_slug);

        const availableList = allPages.filter((page) => {
            if (page.value === 'homepage' && !slugList.includes('homepage')) {
                return true;
            }
            if (page.value === 'inplay' && !slugList.includes('inplay')) {
                return true;
            }
            if (page.value === 'sports' && this.sportListOptions.length !== 0) {
                return true;
            }
            if (page.value === 'casino' && this.casinoListOptions.length !== 0) {
                return true;
            }
            return false;
        });

        return availableList;
    }

    @computed public get cmsTranslationsSeoPagesList(): Array<CmsTranslationDataModelType> {
        return this.cmsTranslationsState.cmsTranslationsList.filter(
            (item) => item.component_type === translationCompontentType
        );
    }

    @action public setSelectedLanguages = (newSelectedLanguages: Array<SelectOptionType>): void => {
        this.allSelectedLanguages = newSelectedLanguages;
    };

    public addLanguageForm = (_e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, id?: number): void => {
        if (id === undefined) {
            const time = new Date().getTime();
            this.formsIds.push(time);
        } else {
            this.formsIds.push(id);
        }
    };

    public getTranslationFormStateById = (formId: number): SeoPageTranslationFormState => {
        return this.translationFormStateMap.get(formId);
    };

    private addTranslations = async (componentId: number): Promise<void> => {
        for (const formId of this.formsIds) {
            const isNewForm = this.getTranslationFormStateById(formId).isNewForm;
            const isDuplicatedForm = this.getTranslationFormStateById(formId).isDuplicatedForm;
            if (isNewForm === true || isDuplicatedForm === true) {
                const form = this.translationFormStateMap.get(formId);
                const formValue = form.getFormValue();
                if (formValue !== null) {
                    await this.handleCreateTranslation(componentId, formValue);
                }
            }
        }
    };

    @action public handleCreateTranslation = async (
        componentId: number,
        formValue: SeoPageTranslationFormType
    ): Promise<void> => {
        const data = {
            component_id: componentId,
            component_type: translationCompontentType,
            language: formValue.language,
            translation: {
                title: formValue.title,
                short_description: formValue.short_description,
                full_description: formValue.full_description,
            },
        };

        const submitResult = await this.cmsTranslationsState.handleCreateApiCall(data);
        if (submitResult === null) {
            console.log('Error creating translation');
            await this.handleError('Something went wrong creating translation.');
        } else {
            await this.cmsTranslationsState.translationsResource.refresh();
        }
    };

    private resetTranslationArrays = (): void => {
        this.allSelectedLanguages = [];
        this.formsIds = [];
    };

    public resetAllTranslationForms = (): void => {
        for (const id of this.formsIds) {
            this.getTranslationFormStateById(id).resetForm();
        }
        this.resetTranslationArrays();
    };

    private validateTranslation = (): boolean => {
        let isValid = true;
        for (const formId of this.formsIds) {
            const isNewForm = this.getTranslationFormStateById(formId).isNewForm;
            const isDuplicatedForm = this.getTranslationFormStateById(formId).isDuplicatedForm;
            if (isNewForm === true || isDuplicatedForm === true) {
                const form = this.translationFormStateMap.get(formId);
                const formValue = form.getFormValue();
                if (formValue === null) {
                    isValid = false;
                }
            }
        }

        return isValid;
    };

    @action public setSeoPagesTranslations = (seoPageTranslation: Array<CmsTranslationDataModelType>): void => {
        this.onEditTranslations = seoPageTranslation;
        for (const translation of this.onEditTranslations) {
            const translatedData = translation.translation;
            if (isTranslation(translatedData)) {
                this.addLanguageForm(null, translation.id);
                const translationForm = this.getTranslationFormStateById(translation.id);
                if (this.isDuplicateMode) {
                    translationForm.isDuplicatedForm = true;
                } else {
                    translationForm.isDuplicatedForm = false;
                }
                translationForm.isNewForm = false;
                translationForm.title.setValue(translatedData.title);
                translationForm.shortDescription.setValue(translatedData.short_description);
                translationForm.fullDescription.setValue(translatedData.full_description);
                translationForm.language.setValue(translation.language);

                const translationLanguage = this.languagesState.getLanguageByValue(translation.language);
                if (translationLanguage !== null) {
                    this.allSelectedLanguages = [translationLanguage, ...this.allSelectedLanguages];
                }
            }
        }
    };

    private editTranslations = async (id: number, formId: number): Promise<void> => {
        const form = this.translationFormStateMap.get(formId);
        const formValue = form.getFormValue();
        if (formValue !== null) {
            await this.handleEditTranslation(formValue, id);
        }
    };

    @action public handleEditTranslation = async (formValue: SeoPageTranslationFormType, id: number): Promise<void> => {
        const data = {
            title: formValue.title,
            short_description: formValue.short_description,
            full_description: formValue.full_description,
            language: formValue.language,
        };

        const submitResult = await this.handleEditTranslationApiCall(data, id);
        if (submitResult === null) {
            console.log('Error');
            await this.handleError('Something went wrong editing translation.');
        } else {
            await this.cmsTranslationsState.translationsResource.refresh();
        }
    };

    @action public handleEditTranslationApiCall = async (
        cmsTranslationForm: SeoPageTranslationFormType,
        id: number
    ): Promise<CmsTranslationFormBodyType | null> => {
        const data = {
            translation: {
                ...cmsTranslationForm,
            },
        };

        try {
            return await apiCommon.updateCmsTranslation.run({ data: data, id });
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    @action public handleDeleteTranslation = async (lang: string): Promise<void> => {
        if (this.isEditMode && this.singleSeoPage !== null) {
            this.deleteFormIds = [...this.deleteFormIds, lang];

            this.removeTranslationsForm(lang);
        } else {
            this.removeTranslationsForm(lang);
        }

        const deletedLanguage = this.allSelectedLanguages.find((item) => item.value === lang);
        if (deletedLanguage !== undefined) {
            const index = this.allSelectedLanguages.indexOf(deletedLanguage);
            this.allSelectedLanguages.splice(index, 1);
        }

        this.showDeletePopup = false;
    };

    @action private removeTranslationsByLang = async (lang: string): Promise<void> => {
        const translation = this.onEditTranslations.find((option) => option.language === lang);
        if (translation !== undefined) {
            await this.trpcClient.client.cmsTranslation.deleteCmsTranslation.mutate({
                id: translation.id,
            });
            await this.cmsTranslationsState.translationsResource.refresh();
            this.getTranslationFormStateById(translation.id).resetForm();
        }
    };

    @action private removeTranslationsForm = (lang: string): void => {
        for (const id of this.formsIds) {
            const form = this.getTranslationFormStateById(id);
            const formLang = form.language.value;
            if (lang === formLang) {
                if (this.isEditMode === false) {
                    form.resetForm();
                }
                this.formsIds = this.formsIds.filter((elem) => elem !== id);
            }
        }
    };

    @action public handleCreate = async (): Promise<void> => {
        this.form.setAsVisited();
        const { result } = this.form;

        if (result.value.type === 'ok') {
            const seoPagesData = {
                ...result.value.data,
                is_active: this.isActive,
            };

            try {
                const submitResult = await this.trpcClient.client.seoPages.createSeoPage.mutate({
                    data: seoPagesData,
                });

                if (submitResult.status === 200) {
                    await this.seoPagesResource.refresh();
                    await this.addTranslations(submitResult.body.id);
                    this.showCreatedPopup = true;
                    this.resetForm();
                } else {
                    return this.handleError('Something went wrong.');
                }
            } catch (error) {
                console.error(error);
            }
        }
    };

    @action public setEditSeoPage = (item: SeoPageType): void => {
        this.singleSeoPage = item;
        this.editedSeoPageId = item.id;
        if (this.isDuplicateMode) {
            this.selectedPage.setValue('');
        } else {
            this.isEditMode = true;
            this.selectedPage.setValue(item.page_slug);
        }
        this.pageSlug.setValue(this.isDuplicateMode ? '' : item.page_slug);
        this.fullDescription.setValue(item.full_description ?? '');
        this.shortDescription.setValue(item.short_description ?? '');
        this.title.setValue(item.title ?? '');
        this.metaTitle.setValue(item.meta_title ?? '');
        this.metaDescription.setValue(item.meta_description ?? '');
        this.isActive = item.is_active;
    };

    @action public handleEdit = async (): Promise<void> => {
        for (const formLang of this.deleteFormIds) {
            await this.removeTranslationsByLang(formLang);
        }

        for (const translation of this.onEditTranslations) {
            await this.editTranslations(translation.id, translation.id);
        }

        if (this.singleSeoPage !== null) {
            if (this.validateTranslation() === false) {
                return;
            }
            await this.addTranslations(this.singleSeoPage.id);
        }
        this.form.setAsVisited();
        const result = this.form.result;

        if (result.value.type === 'ok' && this.singleSeoPage !== null) {
            const sportsDetailsData = {
                ...result.value.data,

                is_active: this.isActive,
            };

            const submitResult = await this.trpcClient.client.seoPages.updateSeoPage.mutate({
                indexNow: this.indexNow,
                data: sportsDetailsData,
                id: this.singleSeoPage.id,
            });
            if (submitResult.status === 200) {
                await this.seoPagesResource.refresh();
                this.resetForm();
            } else {
                console.error('Error');
            }
        }
    };

    @action public resetForm = (): void => {
        this.form.reset();
        this.isEditMode = false;
        this.isDuplicateMode = false;
        this.singleSeoPage = null;
        this.editedSeoPageId = null;
        this.selectedSeoPage = 'homepage';
        this.resetAllTranslationForms();
    };

    @action public handleHideCreatedPopup = (): void => {
        this.showCreatedPopup = false;
    };

    @action public handleShowDeletePopup = (): void => {
        this.showDeletePopup = true;
    };

    @action public handleHideDeletePopup = (): void => {
        this.showDeletePopup = false;
    };

    @action private handleError = async (message: string): Promise<void> => {
        this.errorMessage = message;
        await timeout(5000);
        this.errorMessage = null;
    };
}
